import { useRef, useState } from "react"

import {
  useDeleteUsers,
  useGetFiltersUsers,
  useGetUsers,
  useInviteUsers,
  useResetPasswordUsers,
  useUpdateMembership
} from "@/api/useUsers"
import { useQueryString } from "@/hooks/useQueryString"
import { useTranslation } from "@/hooks/useTranslation"
import {
  getFiltersKeys,
  getFiltersUsersSelector,
  getUsersData
} from "./useUsers.selector"

import type { FilterUsersResponse } from "@/api/useUsers.types"
import type { Params } from "@/hooks/useHttp"
import {
  EditUserRequestPayload,
  InviteUserRequestPayload
} from "./useUsersSettings.types"

export const useUsers = (
  paramKeys?: { key: string }[],
  config?: { enabled: boolean }
) => {
  const [loading, setLoading] = useState(false)
  const params = useQueryString([...(paramKeys || []), ...getFiltersKeys()])
  const paramsOrder = useQueryString(["orderBy", "ascending"])
  const context = useGetUsers(
    { ...params, ...paramsOrder },
    {
      select: getUsersData,
      ...(config || {})
    }
  )

  const { mutateAsync: mutateSendInviteByEmail } = useInviteUsers()
  const { mutateAsync: mutateResetPassword } = useResetPasswordUsers()
  const { mutateAsync: mutateDeleteUser } = useDeleteUsers()
  const { mutateAsync: mutateUpdateMembership } = useUpdateMembership()

  const wrapFn =
    (fn: any) =>
    async (...args: any) => {
      try {
        setLoading(true)

        const result = await fn(...args)
        const data = result?.data

        return { err: null, data }
      } catch (err: any) {
        return { err: true, data: err?.data }
      } finally {
        setLoading(false)
      }
    }

  const sendInviteByEmail = wrapFn(
    async (payload: InviteUserRequestPayload) => {
      const result = await mutateSendInviteByEmail(payload)
      await context.refetch()

      return result
    }
  )

  const resetPassword = wrapFn(async (email: string) => {
    const result = await mutateResetPassword({ user_email: email })

    return result
  })

  const updateMembership = wrapFn(
    async (id: string, payload: EditUserRequestPayload) => {
      await mutateUpdateMembership({ needle: `/${id}`, data: payload })
      await context.refetch()
    }
  )

  const deleteUser = async (userId: string) => {
    await mutateDeleteUser(userId)
    await context.refetch()
  }

  return [
    context,
    { loading, deleteUser, resetPassword, sendInviteByEmail, updateMembership }
  ] as const
}

export const useFiltersUsers = (params?: Params) => {
  const { t } = useTranslation()
  const select = useRef((response: FilterUsersResponse) =>
    getFiltersUsersSelector(response, t)
  ).current
  const context = useGetFiltersUsers(params, { select })

  return context
}
